import { Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { MyrAPIService } from '../../../api/myr/myrAPI';
import { MyrAPIEndpoints } from '../../../api/myr/myrAPIEndpoints.constant';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../../app/common/errorResolution/errorResolution';
import { ErrorResolutionActions } from '../../../app/common/errorResolution/errorResolutionActions';
import { NotificationsActions } from '../../../app/common/notifications/notificationsActions';
import { NotificationAction } from '../../../app/common/notifications/notificationsActionType';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IAddress, IAddressRequest } from '../../addresses/address/address.interface';
import { AddressesActions } from '../../addresses/addressesActions';
import { ICard, ICardPostRequest, ICardPutRequest } from './card/card.interface';
import { CardsAction, CardsActionType } from './cardsActionType';
import { ICardsState } from './cardsState';
import ErrorRedirectService from '../../../routes/error/errorRedirect.service';
import { addRatEvent } from '../../../utils/ratClickEvents';

function hasErrorCode(
    error: ErrorResolution,
): error is Extract<ErrorResolution, { errorCode: string }> {
    return (
        (error.type === ErrorResolutionTypes.WARNING_CODE_MESSAGE ||
            error.type === ErrorResolutionTypes.ERROR_LIST ||
            error.type === ErrorResolutionTypes.ERROR_BOX) &&
        'errorCode' in error
    );
}

export class CardsActions {
    public static addCard(newCard: ICardPostRequest): any {
        const endpoint = MyrAPIEndpoints.CARD;

        const request = () => ({
            payload: {},
            type: CardsActionType.ADD_CARD_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            card: ICard,
        ) => {
            addRatEvent('payment_method', 'add_card_success_be');
            dispatch({
                payload: { card },
                type: CardsActionType.ADD_CARD_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.card.add.success'),
                ),
            );
            dispatch(CardsActions.getCards());
        };

        const resolveError = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            if (hasErrorCode(errorResolution) && errorResolution.errorCode === 'rid-1004') {
                addRatEvent('payment_method', 'add_card_already_exist');
            } else {
                addRatEvent('payment_method', 'add_card_failed_be');
            }

            dispatch({
                payload: { errorResolution },
                type: CardsActionType.ADD_CARD_FAILURE,
            });

            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(CardsActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.card.add.failure',
                        { challengerPid: newCard.challengeData.cpid },
                    ),
                );
            }
        };

        return (dispatch: Dispatch<CardsAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<ICard>(endpoint, 'POST', newCard)
                .then((json: ICard) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static addCardWithNewAddress(
        newCard: ICardPutRequest | ICardPostRequest,
        newAddress: IAddressRequest,
        newCardToken = '',
    ): any {
        return (dispatch: ThunkDispatch<ICardsState, {}, CardsAction>) => {
            return dispatch(AddressesActions.addAddress(newAddress)).then((address: IAddress) => {
                if (newCardToken) {
                    const card = {
                        ...newCard,
                        addressUuid: address.addressUuid,
                    } as ICardPutRequest;
                    dispatch(CardsActions.updateCard(newCardToken, card));
                } else {
                    const card = {
                        ...newCard,
                        addressUuid: address.addressUuid,
                    } as ICardPostRequest;
                    dispatch(CardsActions.addCard(card));
                }
            });
        };
    }

    public static deleteCard(cardToken: string): any {
        const endpoint = `${MyrAPIEndpoints.CARD}/${cardToken}`;

        const request = () => ({
            payload: {},
            type: CardsActionType.REMOVE_CARD_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            json: { success: boolean },
        ) => {
            addRatEvent('payment_method', 'delete_card_success_be');
            dispatch({
                // Response body should always have success: true
                payload: { cardToken },
                type: CardsActionType.REMOVE_CARD_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.card.delete.success'),
                ),
            );
            dispatch(CardsActions.getCards());
        };

        const resolveError = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            addRatEvent('payment_method', 'delete_card_failed_be');
            dispatch({
                payload: { errorResolution },
                type: CardsActionType.REMOVE_CARD_FAILURE,
            });
            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(CardsActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.card.delete.failure',
                    ),
                );
            }
        };

        return (dispatch: Dispatch<CardsAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<{ success: boolean }>(endpoint, 'DELETE')
                .then((json: { success: boolean }) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static getCards(): any {
        const endpoint = MyrAPIEndpoints.CARD;

        const request = () => ({
            payload: {},
            type: CardsActionType.GET_CARDS_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            cards: ICard[],
        ) => {
            cards.sort((a, b) => {
                return a['isPrimary'] ? -1 : b['isPrimary'] ? 1 : 0;
            });
            dispatch({
                payload: { cards },
                type: CardsActionType.GET_CARDS_SUCCESS,
            });
        };

        const resolveError = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            dispatch({
                payload: { errorResolution },
                type: CardsActionType.GET_CARDS_FAILURE,
            });
            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(CardsActions.closeForm());
            }
        };

        return (dispatch: Dispatch<CardsAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<ICard[]>(endpoint, 'GET')
                .then((json: ICard[]) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static setIsPrimary(cardToken: string, updatedCard: ICardPutRequest): any {
        const endpoint = `${MyrAPIEndpoints.CARD}/${cardToken}`;

        const request = () => ({
            payload: {},
            type: CardsActionType.UPDATE_CARD_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            card: ICard,
        ) => {
            addRatEvent('payment_method', 'set_default_card_success_be');
            dispatch({
                payload: { card },
                type: CardsActionType.UPDATE_CARD_SUCCESS,
            });

            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.card.update.success'),
                ),
            );
            dispatch(CardsActions.getCards());
        };

        const resolveError = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            addRatEvent('payment_method', 'set_default_card_failed_be');
            dispatch({
                payload: { errorResolution },
                type: CardsActionType.UPDATE_CARD_FAILURE,
            });

            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(CardsActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.card.update.failure',
                        { challengerPid: updatedCard.challengeData?.cpid },
                    ),
                );
            }
        };

        return (dispatch: Dispatch<CardsAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<ICard>(endpoint, 'PUT', updatedCard)
                .then((json: ICard) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static updateCard(cardToken: string, updatedCard: ICardPutRequest): any {
        const endpoint = `${MyrAPIEndpoints.CARD}/${cardToken}`;

        const request = () => ({
            payload: {},
            type: CardsActionType.UPDATE_CARD_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            card: ICard,
        ) => {
            addRatEvent('payment_method', 'edit_card_success_be');
            dispatch({
                payload: { card },
                type: CardsActionType.UPDATE_CARD_SUCCESS,
            });

            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.card.update.success'),
                ),
            );
            dispatch(CardsActions.getCards());
        };

        const resolveError = (
            dispatch: Dispatch<CardsAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            addRatEvent('payment_method', 'edit_card_failed_be');
            dispatch({
                payload: { errorResolution },
                type: CardsActionType.UPDATE_CARD_FAILURE,
            });

            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(CardsActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.card.update.failure',
                        { challengerPid: updatedCard.challengeData?.cpid },
                    ),
                );
            }
        };

        return (dispatch: Dispatch<CardsAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<ICard>(endpoint, 'PUT', updatedCard)
                .then((json: ICard) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static openForm(): CardsAction {
        return {
            payload: {},
            type: CardsActionType.OPEN_CARD_FORM,
        };
    }

    public static closeForm(): CardsAction {
        return {
            payload: {},
            type: CardsActionType.CLOSE_CARD_FORM,
        };
    }
}
